import React, { useEffect, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { journeyActions } from "store/actions";
import { LoginContext } from "helpers/LoginContext";
import SideBar from "components/common/SideBar/SideBarNew";
import Header from "components/common/Header/Header";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import { HiArrowLeft } from "react-icons/hi";
import {
  getJourneySessionDetail,
  getJourneyDetailLoading,
  getJourneyMoreLoading,
  getJourneyError,
} from "store/selector/journey.selector";
import {
  formatDateTimeInClientTimezone,
  formatTimeInClientTimezone,
} from "Utils/utils";

const EVENT_CONFIG = {
  page_view: { icon: "\uD83D\uDCC4", badge: "bg-blue-100 text-blue-700", label: "Page View" },
  audit: { icon: "\u270F\uFE0F", badge: "bg-purple-100 text-purple-700", label: "Audit Action" },
  session_start: { icon: "\uD83D\uDD11", badge: "bg-green-100 text-green-700", label: "Session Start" },
  session_end: { icon: "\uD83D\uDEAA", badge: "bg-red-100 text-red-700", label: "Session End" },
  search: { icon: "\uD83D\uDD0D", badge: "bg-yellow-100 text-yellow-700", label: "Search" },
  modal_open: { icon: "\u26A0\uFE0F", badge: "bg-orange-100 text-orange-700", label: "Modal Open" },
  feature_use: { icon: "\u2728", badge: "bg-cyan-100 text-cyan-700", label: "Feature Use" },
  tab_switch: { icon: "\u21C4", badge: "bg-indigo-100 text-indigo-700", label: "Tab Switch" },
  crud_operation: { icon: "\u2699\uFE0F", badge: "bg-amber-100 text-amber-700", label: "CRUD" },
  api_request: { icon: "\uD83D\uDCE1", badge: "bg-slate-100 text-slate-700", label: "API Request" },
  form_submit: { icon: "\u2705", badge: "bg-emerald-100 text-emerald-700", label: "Form Submit" },
  form_validation_error: { icon: "\u274C", badge: "bg-rose-100 text-rose-700", label: "Validation Error" },
};

const DEFAULT_EVENT = { icon: "\u2022", badge: "bg-gray-100 text-gray-700", label: "Event" };

const formatDateTime = (dateStr) => formatDateTimeInClientTimezone(dateStr);

const formatTime = (dateStr) => formatTimeInClientTimezone(dateStr);

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "\u2014";
  const s = Number(seconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}h ${m}m`;
};

const formatEntityType = (val) =>
  val
    ? val.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "";

const getEventDescription = (event) => {
  if (event.source === "audit") {
    const parts = [];
    if (event.entity_type) parts.push(formatEntityType(event.entity_type));
    if (event.audit_action) parts.push(formatEntityType(event.audit_action));
    if (event.description) parts.push(`\u2014 ${event.description}`);
    return parts.join(" ") || "Audit action";
  }
  if (event.event_type === "page_view") {
    return event.route || event.page_title || "Page navigation";
  }
  if (event.event_type === "search") {
    return event.query ? `Searched: "${event.query}"` : "Search performed";
  }
  if (event.event_type === "modal_open") {
    return event.modal_name || "Modal opened";
  }
  if (event.event_type === "session_start") {
    return "Session started";
  }
  if (event.event_type === "session_end") {
    return "Session ended";
  }
  if (event.event_type === "feature_use") {
    const feat = event.event_data?.feature;
    return feat ? `Used feature: ${feat}` : "Feature used";
  }
  if (event.event_type === "tab_switch") {
    const d = event.event_data;
    return d?.from_tab && d?.to_tab
      ? `Switched from ${d.from_tab} to ${d.to_tab}`
      : "Tab switched";
  }
  if (event.event_type === "crud_operation") {
    return event.event_data?.action || "CRUD operation";
  }
  if (event.event_type === "api_request") {
    const d = event.event_data;
    return d ? `${d.method || "?"} ${d.path || ""}` : "API request";
  }
  if (event.event_type === "form_submit") {
    return event.event_data?.entity_type ? `Form submitted: ${event.event_data.entity_type}` : "Form submitted";
  }
  if (event.event_type === "form_validation_error") {
    const fields = event.event_data?.error_fields;
    return fields?.length ? `Validation error: ${fields.join(", ")}` : "Validation error";
  }
  return event.description || event.event_type || "Unknown event";
};

const SessionTimeline = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const { sideBarPadding } = useContext(LoginContext);

  const detailLoading = useSelector(getJourneyDetailLoading);
  const moreLoading = useSelector(getJourneyMoreLoading);
  const sessionDetail = useSelector(getJourneySessionDetail);
  const error = useSelector(getJourneyError);

  useEffect(() => {
    if (sessionId) {
      dispatch(journeyActions.getJourneySessionDetailRequest(sessionId));
    }
    return () => {
      dispatch(journeyActions.resetJourneySessionDetail());
    };
  }, [dispatch, sessionId]);

  const session = sessionDetail?.session || {};
  const timeline = sessionDetail?.timeline || [];
  const pagination = sessionDetail?.timeline_pagination || {};
  const hasMore = pagination.has_more;
  const totalEvents = session.total_events ?? timeline.length;

  const handleLoadMore = () => {
    if (!sessionId || moreLoading || !hasMore) return;
    const nextPage = (pagination.page || 1) + 1;
    dispatch(
      journeyActions.getJourneySessionDetailRequest({
        sessionId,
        page: nextPage,
        per_page: pagination.per_page || 25,
        append: true,
      })
    );
  };

  return (
    <div className="min-h-full">
      <SideBar />
      <main>
        <div
          className={`${
            sideBarPadding
              ? "lg:pl-[4rem] duration-300 flex flex-col"
              : "lg:pl-64 duration-300 flex flex-col"
          }`}
        >
          <Header
            title="Session Timeline"
            desc={session.user_name ? `Session for ${session.user_name}` : "Session detail"}
          />

          <div className="w-full">
            {/* Back Button */}
            <div className="bg-white border-b px-4 sm:px-6 lg:px-8 py-3">
              <button
                onClick={() => navigate("/user-sessions")}
                className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              >
                <HiArrowLeft className="w-4 h-4" />
                Back to Sessions
              </button>
            </div>

            {detailLoading ? (
              <div className="bg-white py-12">
                <CircularLoader
                  classes="flex justify-center items-center"
                  classloader="loader-l"
                />
              </div>
            ) : sessionDetail ? (
              <>
                {/* Session Summary */}
                <div className="bg-white border-b px-4 sm:px-6 lg:px-8 py-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </p>
                      <p className="mt-1 text-sm font-semibold text-gray-900 capitalize">
                        {session.user_name || "\u2014"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Started
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDateTime(session.started_at)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {formatDuration(session.duration_seconds)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Events
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {totalEvents}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Audit Actions
                      </p>
                      <p className="mt-1 text-sm text-gray-900">
                        {session.audit_actions_count ?? "\u2014"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                        IP Address
                      </p>
                      <p className="mt-1 text-sm text-gray-400 font-mono">
                        {session.ip_address || "\u2014"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Timeline */}
                <div className="bg-white max-w-full mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-10">
                  {timeline.length > 0 ? (
                    <div className="flow-root">
                      <ul className="-mb-8">
                        {timeline.map((event, idx) => {
                          const config =
                            EVENT_CONFIG[event.event_type] || DEFAULT_EVENT;
                          const isLast = idx === timeline.length - 1;
                          return (
                            <li key={event.id || idx}>
                              <div className="relative pb-8">
                                {!isLast && (
                                  <span
                                    className="absolute top-5 left-5 -ml-px h-full w-0.5 bg-gray-200"
                                    aria-hidden="true"
                                  />
                                )}
                                <div className="relative flex items-start space-x-3">
                                  {/* Icon */}
                                  <div className="relative">
                                    <span className="h-10 w-10 rounded-full flex items-center justify-center ring-8 ring-white bg-gray-50 text-lg">
                                      {config.icon}
                                    </span>
                                  </div>

                                  {/* Content */}
                                  <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${config.badge}`}
                                      >
                                        {config.label}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                        {formatTime(event.client_timestamp || event.created_at)}
                                      </span>
                                    </div>
                                    <p className="mt-1 text-sm text-gray-700">
                                      {getEventDescription(event)}
                                    </p>
                                    {event.source === "audit" && event.entity_type && (
                                      <div className="mt-1 flex items-center gap-2">
                                        <span className="text-xs text-gray-400">
                                          {formatEntityType(event.entity_type)}
                                        </span>
                                        {event.audit_action && (
                                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600">
                                            {formatEntityType(event.audit_action)}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ) : (
                    <div className="py-12 text-center text-gray-400">
                      No timeline events for this session.
                    </div>
                  )}
                  {hasMore && (
                    <div className="mt-6 flex justify-center">
                      <button
                        onClick={handleLoadMore}
                        disabled={moreLoading}
                        className="px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-md hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {moreLoading ? "Loading..." : "Load more events"}
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="bg-white py-12 px-4 text-center">
                <p className="text-gray-500">Session not found.</p>
                {error && (
                  <p className="mt-2 text-sm text-red-600 max-w-lg mx-auto">
                    {error?.response?.data?.message || error?.message || String(error)}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SessionTimeline;
